/**
 * The HTTP edge for the driver view. COOKIES, like every portal in this app -
 * never a token in browser storage (SECURITY_RULES 1-2).
 *
 * The driver view lives inside the Delivery Portal at app.redbridgefeed.com;
 * the API is api.redbridgefeed.com. The staff login sets httpOnly cookies on
 * .redbridgefeed.com with SameSite=None; Secure, so every request here - from
 * the page AND from the service worker's Background Sync drain - sends them
 * with credentials: 'include'.
 *
 * On a 401 it calls the cookie refresh once (the refresh_token cookie renews
 * the one-day access cookie) and retries. If that fails too, the request fails
 * with a 401 ApiError: the queue keeps the stop and says "sign in to upload".
 *
 * Runs in the page and in the service worker, so nothing here touches
 * `window` or `document`.
 */
import { getMeta } from './db';
import type { CompletionRecord, FindingRecord, PhotoRecord } from './types';

function normalise(raw: string | undefined | null): string {
  return (raw || '').replace(/\.+\/*$/, '').replace(/\/+$/, '');
}

/** Baked in at build time for the page. The service worker may not see build
 *  env, so the page also records the base in IndexedDB (DriverApp) and this
 *  falls back to it. */
const ENV_BASE = normalise(process.env.REACT_APP_API_URL);

export const API_BASE_FOR_PAGE = ENV_BASE || 'http://localhost:8000/api';

export async function apiBase(): Promise<string> {
  if (ENV_BASE) return ENV_BASE;
  const saved = normalise(await getMeta<string>('apiBase'));
  return saved || 'http://localhost:8000/api';
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `HTTP ${status}`);
  }
}

/** A fetch that never got a response: offline, DNS, refused, aborted. */
export class NetworkError extends Error {}

async function rawFetch(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, { ...init, credentials: 'include' });
  } catch (err) {
    throw new NetworkError(err instanceof Error ? err.message : String(err));
  }
}

/** Renew the access cookie from the refresh cookie. True if it worked. */
export async function refreshSession(): Promise<boolean> {
  try {
    const res = await rawFetch(`${await apiBase()}/auth/cookie-refresh/`, { method: 'POST' });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Authenticated fetch. Retries ONCE on 401 after a cookie refresh.
 * Throws NetworkError when there was no response at all; otherwise returns the
 * response and the caller turns a non-2xx into an ApiError.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = `${await apiBase()}${path}`;
  let res = await rawFetch(url, init);
  if (res.status === 401 && (await refreshSession())) {
    res = await rawFetch(url, init);
  }
  return res;
}

async function bodyOf(res: Response): Promise<unknown> {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text;
  }
}

function failure(res: Response, body: unknown): ApiError {
  if (res.status === 401) return new ApiError(401, body, 'Signed out - sign in to upload');
  const msg = (body as { error?: string } | null)?.error;
  return new ApiError(res.status, body, msg ?? `HTTP ${res.status}`);
}

export async function fetchRoute<T = unknown>(): Promise<T | null> {
  const res = await apiFetch('/delivery/driver/route/');
  const body = (await bodyOf(res)) as { route: T | null } | null;
  if (!res.ok) throw failure(res, body);
  return body?.route ?? null;
}

export type UploadResult = 'synced' | 'superseded';

export async function postCompletion(rec: CompletionRecord): Promise<UploadResult> {
  // The completion id rides IN THE BODY, not a custom header: a custom header
  // forces a CORS preflight the API's allow-list would have to name - found in
  // the first live offline proof (2026-09-09).
  const res = await apiFetch(`/delivery/driver/stops/${rec.stopUuid}/complete/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...rec.payload, completion_id: rec.completionId }),
  });
  const body = (await bodyOf(res)) as { result?: string } | null;
  if (!res.ok) throw failure(res, body);
  return body?.result === 'superseded' ? 'superseded' : 'synced';
}

export async function putPhoto(rec: PhotoRecord): Promise<void> {
  const form = new FormData();
  form.append('photo', rec.blob, `${rec.itemType}.jpg`);
  form.append('captured_at', rec.capturedAt);
  const res = await apiFetch(`/delivery/driver/stops/${rec.stopUuid}/photos/${rec.itemType}/`, {
    method: 'PUT',
    body: form,
  });
  if (!res.ok) throw failure(res, await bodyOf(res));
}

/** What the driver found at one location (delivery/driver_findings.py). The id rides in
 *  the body as client_write_id - never a custom header (see postCompletion). A late,
 *  older note comes back 'superseded' with 200; the server keeps the newer one. */
export async function putFoundNote(rec: FindingRecord): Promise<void> {
  const res = await apiFetch(`/delivery/driver/stops/${rec.stopUuid}/found/${rec.location}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: rec.text, client_write_id: rec.id, captured_at: rec.capturedAt }),
  });
  if (!res.ok) throw failure(res, await bodyOf(res));
}

/** A reference photo the driver took (not a proof photo). Deduped on client_write_id. */
export async function postReferencePhoto(rec: FindingRecord): Promise<void> {
  const form = new FormData();
  form.append('kind', rec.photoKind);
  form.append('client_write_id', rec.id);
  form.append('captured_at', rec.capturedAt);
  if (rec.blob) form.append('image', rec.blob, `${rec.photoKind}.jpg`);
  const res = await apiFetch(`/delivery/driver/stops/${rec.stopUuid}/reference-photos/`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) throw failure(res, await bodyOf(res));
}
